import * as unset from "unset-value";

export declare type Maybe<T> = T | null | undefined;
export type NonNullableKeys<T, K extends keyof T> = {
  [P in K]-?: NonNullable<T[P]>;
};
export type RequiredKeepUndefined<T> = {
  [K in keyof T]-?: [T[K]];
} extends infer U // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ? U extends Record<keyof U, [any]>
    ? {
        [K in keyof U]: U[K][0];
      }
    : never
  : never;
export type MergeIntersection<T> = {
  [K in keyof T]: T[K];
};
export type NeededOnly<T, K extends keyof T | never, S extends keyof T | never = never> = {
  [X in keyof T]?: Maybe<T[X]>;
} &
  RequiredKeepUndefined<Pick<T, K>> &
  NonNullableKeys<T, S>;
export type Needed<T, K extends keyof T | never = never, S extends keyof T | never = never> = {
  [X in keyof T]: T[X];
} &
  RequiredKeepUndefined<Pick<T, K>> &
  NonNullableKeys<T, S>;

export const isObject = (value: any) => value !== null && typeof value === "object";

export const omitDeep = (value: any, keys: string[] | string) => {
  if (typeof value === "undefined") {
    return {};
  }

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = omitDeep(value[i], keys);
    }
    return value;
  }

  if (!isObject(value)) {
    return value;
  }

  if (typeof keys === "string") {
    keys = [keys];
  }

  if (!Array.isArray(keys)) {
    return value;
  }

  for (let j = 0; j < keys.length; j += 1) {
    unset(value, keys[j]);
  }

  for (const key in value) {
    if (value.hasOwnProperty(key)) {
      value[key] = omitDeep(value[key], keys);
    }
  }

  return value;
};

export const removeUndefined = (object: any) => {
  if (object) {
    Object.keys(object).forEach(key => {
      if (object[key] === undefined) {
        delete object[key];
      } else if (typeof object[key] === "object") {
        object[key] = removeUndefined(object[key]);
      }
    });
  }
  return object;
};

export const inEnum = <T extends object>(enu: T, value: any) => Object.values(enu).includes(value);
